import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiDisclosuresData,
  type EnrollmentMultiDisclosuresSubmitInput,
  type EnrollmentMultiDisclosuresSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi Disclosures data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiDisclosuresData>
 * @example const response = await getEnrollmentMultiDisclosuresData()
 */
export const getEnrollmentMultiDisclosuresData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiDisclosuresData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiDisclosuresData',
      query: `mutation getEnrollmentMultiDisclosuresData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              progress
              userInfo {
                investment {
                  disclosures {
                    disclosureId
                    disclosureName
                    disclosureUri
                  }
                }
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiDisclosuresData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiDisclosuresData = createQueryHook<undefined, EnrollmentMultiDisclosuresData>(
  getEnrollmentMultiDisclosuresData,
)

/**
 * Submit Enrollment Multi Disclosures data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiDisclosuresSubmitOutput>
 * @example const response = await submitEnrollmentMultiDisclosuresData();
 */
export const submitEnrollmentMultiDisclosuresData = async (
  enrollmentDisclosuresSubmitInput: EnrollmentMultiDisclosuresSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiDisclosuresSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiDisclosuresData',
      query: `mutation submitEnrollmentMultiDisclosuresData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentDisclosuresSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiDisclosuresSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiDisclosuresData = createMutationHook<
  EnrollmentMultiDisclosuresSubmitInput,
  EnrollmentMultiDisclosuresSubmitOutput
>(submitEnrollmentMultiDisclosuresData)
